import { posthog } from '@/features/flags/posthog-compat';
import { logger } from '@/utils/logger/logger';

/**
 * Cerner MPage / iframe mic diagnostics — web embed only (not Tauri capture path).
 */
/* eslint-disable @heidi/rules/no-unguarded-browser-media -- web-only Cerner embedded browser troubleshooting */

/**
 * PostHog flag for temporary WHTH / Cerner embed mic troubleshooting.
 * Intentionally PostHog (per integration rollout); remove after embed issue is resolved.
 */
export const CERNER_EMBEDDED_BROWSER_DIAGNOSTICS_FLAG =
  'config-cerner-embedded-browser-diagnostics';

export const CERNER_EMBEDDED_DIAGNOSTICS_LOG_PREFIX = '[CERNER_EMBEDDED_DIAGNOSTICS]';

export type CernerEmbeddedDiagnosticsPhase =
  | 'launch_mount'
  | 'record_button_pre_get_user_media'
  | 'record_button_get_user_media_result';

export type CernerEmbeddedEnvironmentSnapshot = {
  isEmbedded: boolean;
  isSecureContext: boolean;
  hasMediaDevices: boolean;
  hasGetUserMedia: boolean;
  permissionsPolicyAllowsMicrophone: boolean | null;
  featurePolicyAllowsMicrophone: boolean | null;
  permissionsQuerySupported: boolean;
  microphonePermissionState: string | null;
  browserFamily: string;
  locationHost: string | null;
  referrerHost: string | null;
};

export type CernerEmbeddedGetUserMediaResult = {
  ok: boolean;
  errorName?: string;
  audioTrackCount?: number;
};

type CernerEmbeddedDiagnosticsContext = {
  issHostname?: string | null;
  ehrProvider?: string;
};

export const isCernerEmbeddedBrowserDiagnosticsEnabled = (): boolean =>
  posthog.isFeatureEnabled(CERNER_EMBEDDED_BROWSER_DIAGNOSTICS_FLAG) === true;

export const resolveCoarseBrowserFamily = (userAgent: string): string => {
  if (/Edg\//.test(userAgent)) {
    return 'Edge';
  }
  if (/Chrome\//.test(userAgent)) {
    return 'Chrome';
  }
  if (/Firefox\//.test(userAgent)) {
    return 'Firefox';
  }
  if (/Safari\//.test(userAgent)) {
    return 'Safari';
  }
  return 'Other';
};

export const safeUrlHostname = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const readPermissionsPolicyAllowsMicrophone = (): boolean | null => {
  const permissionsPolicy = (
    document as Document & {
      permissionsPolicy?: { allowsFeature: (feature: string) => boolean };
    }
  ).permissionsPolicy;
  if (permissionsPolicy?.allowsFeature) {
    return permissionsPolicy.allowsFeature('microphone');
  }
  return null;
};

const readFeaturePolicyAllowsMicrophone = (): boolean | null => {
  const featurePolicy = (
    document as Document & {
      featurePolicy?: { allowsFeature: (feature: string) => boolean };
    }
  ).featurePolicy;
  if (featurePolicy?.allowsFeature) {
    return featurePolicy.allowsFeature('microphone');
  }
  return null;
};

const buildCernerEmbeddedEnvironmentSnapshotBase = () => {
  const mediaDevices = navigator.mediaDevices;
  const hasGetUserMedia = typeof mediaDevices?.getUserMedia === 'function';

  return {
    isEmbedded: window.self !== window.top,
    isSecureContext: window.isSecureContext,
    hasMediaDevices: Boolean(mediaDevices),
    hasGetUserMedia,
    permissionsPolicyAllowsMicrophone: readPermissionsPolicyAllowsMicrophone(),
    featurePolicyAllowsMicrophone: readFeaturePolicyAllowsMicrophone(),
    permissionsQuerySupported: typeof navigator.permissions?.query === 'function',
    browserFamily: resolveCoarseBrowserFamily(navigator.userAgent),
    locationHost: window.location.hostname || null,
    referrerHost: safeUrlHostname(document.referrer),
  };
};

export const collectCernerEmbeddedEnvironmentSnapshotSync =
  (): CernerEmbeddedEnvironmentSnapshot => ({
    ...buildCernerEmbeddedEnvironmentSnapshotBase(),
    microphonePermissionState: null,
  });

export const collectCernerEmbeddedEnvironmentSnapshot =
  async (): Promise<CernerEmbeddedEnvironmentSnapshot> => {
    let microphonePermissionState: string | null = null;
    const permissions = navigator.permissions;
    const permissionsQuery =
      permissions && typeof permissions.query === 'function'
        ? (descriptor: PermissionDescriptor) => permissions.query(descriptor)
        : undefined;

    if (permissionsQuery) {
      try {
        const status = await permissionsQuery({
          name: 'microphone' as PermissionName,
        });
        microphonePermissionState = status.state;
      } catch {
        microphonePermissionState = 'query_failed';
      }
    }

    return {
      ...buildCernerEmbeddedEnvironmentSnapshotBase(),
      microphonePermissionState,
    };
  };

export const logCernerEmbeddedDiagnostics = (
  phase: CernerEmbeddedDiagnosticsPhase,
  snapshot: CernerEmbeddedEnvironmentSnapshot,
  context: CernerEmbeddedDiagnosticsContext = {},
  getUserMediaResult?: CernerEmbeddedGetUserMediaResult
): void => {
  logger.info(CERNER_EMBEDDED_DIAGNOSTICS_LOG_PREFIX, {
    phase,
    ...snapshot,
    issHostname: context.issHostname ?? null,
    ehrProvider: context.ehrProvider ?? null,
    microphoneProbeOk: getUserMediaResult?.ok ?? null,
    microphoneProbeErrorName: getUserMediaResult?.errorName ?? null,
    microphoneProbeAudioTrackCount: getUserMediaResult?.audioTrackCount ?? null,
  });
};

export const runCernerEmbeddedLaunchDiagnostics = async (context: {
  iss?: string | null;
  ehrProvider?: string;
}): Promise<void> => {
  if (!isCernerEmbeddedBrowserDiagnosticsEnabled()) {
    return;
  }

  try {
    const snapshot = await collectCernerEmbeddedEnvironmentSnapshot();
    logCernerEmbeddedDiagnostics('launch_mount', snapshot, {
      issHostname: safeUrlHostname(context.iss ?? null),
      ehrProvider: context.ehrProvider,
    });
  } catch {
    // Launch diagnostics must never block SMART bootstrap.
  }
};

export const runCernerEmbeddedRecordButtonDiagnostics = (): void => {
  if (!isCernerEmbeddedBrowserDiagnosticsEnabled()) {
    return;
  }

  try {
    const snapshot = collectCernerEmbeddedEnvironmentSnapshotSync();
    logCernerEmbeddedDiagnostics('record_button_pre_get_user_media', snapshot);
  } catch {
    // Record start must never depend on diagnostics.
  }
};

export const logCernerEmbeddedRecordButtonGetUserMediaResult = (
  result: CernerEmbeddedGetUserMediaResult
): void => {
  if (!isCernerEmbeddedBrowserDiagnosticsEnabled()) {
    return;
  }

  try {
    const snapshot = collectCernerEmbeddedEnvironmentSnapshotSync();
    logCernerEmbeddedDiagnostics(
      'record_button_get_user_media_result',
      snapshot,
      {},
      result
    );
  } catch {
    // Result logging must never affect record error handling.
  }
};
/* eslint-enable @heidi/rules/no-unguarded-browser-media */
